import { useCallback, useEffect, useRef, useState } from 'react';
import { listQmsTags } from '../api/listQmsTagsApiService';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 1;

export interface UseQmsTagSuggestionsResult {
  suggestions: string[];
  isLoading: boolean;
  search: string;
  setSearch: (value: string) => void;
  clearSuggestions: () => void;
}

export const useQmsTagSuggestions = (): UseQmsTagSuggestionsResult => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearchState] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef(false);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    abortRef.current = false;

    const result = await listQmsTags({ search: query, limit: 10, page: 1 });

    if (!abortRef.current) {
      setSuggestions(result.success ? result.tags.map((t) => t.name) : []);
      setIsLoading(false);
    }
  }, []);

  const setSearch = useCallback(
    (value: string) => {
      setSearchState(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (value.length < MIN_SEARCH_LENGTH) {
        abortRef.current = true;
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      debounceRef.current = setTimeout(() => {
        fetchSuggestions(value);
      }, DEBOUNCE_MS);
    },
    [fetchSuggestions]
  );

  const clearSuggestions = useCallback(() => {
    abortRef.current = true;
    setSuggestions([]);
    setIsLoading(false);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return { suggestions, isLoading, search, setSearch, clearSuggestions };
};
